import { ApolloClient, useApolloClient } from "@apollo/client";
import { useConsultationActionMutation } from "apollo/hooks/mutations";
import {
  CLINICAL_CONSULTATION_ACTION,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import {
  AuctionForm,
  ClinicalConsultation,
  ClinicalConsultationActionType,
  EncryptionContext,
  Patient,
  QueryProgress,
  RecareFileType,
} from "core/types";
import { useCallback, useState } from "react";

export type ConsultationActionPayloads = {
  [CLINICAL_CONSULTATION_ACTION.GENERATE_FORM]: {
    clinical_consultation_id: number;
    context: {
      clinical_consultation: {
        id: number;
        forms?: Array<Pick<AuctionForm, "form_type"> & Partial<AuctionForm>>;
        seald_encryption_context?: EncryptionContext;
      };
    };
  };
  [CLINICAL_CONSULTATION_ACTION.DELETE_FORM]: {
    id?: number;
    context: {
      form_type: RecareFileType;
    };
  };
};

type ActionPayload<A extends ClinicalConsultationActionType> =
  A extends keyof ConsultationActionPayloads
    ? ConsultationActionPayloads[A]
    : never;

type ActionResults = {
  [CLINICAL_CONSULTATION_ACTION.GENERATE_FORM]: ClinicalConsultation;
  [CLINICAL_CONSULTATION_ACTION.DELETE_FORM]: ClinicalConsultation;
};

type ActionResult<A extends ClinicalConsultationActionType> =
  A extends keyof ActionResults ? ActionResults[A] : ClinicalConsultation;

export type ConsultationActionFunction<
  A extends ClinicalConsultationActionType,
> = (args: CallConsultationActionProps<A>) => Promise<ActionResult<A> | null>;

type UseConsultationActionReturnType<A extends ClinicalConsultationActionType> =
  [ConsultationActionFunction<A>, QueryProgress, () => void];

type CallConsultationActionProps<A extends ClinicalConsultationActionType> = {
  consultation: ClinicalConsultation;
  patient: Patient;
  context: ActionPayload<A>["context"];
  id?: number;
  onCompleted?: (
    result: ActionResult<A> | null,
    client: ApolloClient<object>,
  ) => void;
  onError?: (error: unknown) => void;
};

export const useClinicalConsultationAction = <
  A extends ClinicalConsultationActionType,
>({
  actionType,
}: {
  actionType: A;
}): UseConsultationActionReturnType<A> => {
  const client = useApolloClient();
  const [consultationAction, , resetProgress] = useConsultationActionMutation({
    actionType,
  });

  const [queryProgress, setQueryProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const callAction = useCallback(
    async ({
      consultation,
      context,
      onCompleted,
      onError,
      patient,
    }: CallConsultationActionProps<A>): Promise<ActionResult<A> | null> => {
      setQueryProgress(QUERY_PROGRESS_PENDING);
      if (!patient?.id) {
        const error = `Patient passed to useClinicalConsultationAction is missing for actionType ${actionType}`;
        console.error(error);
        setQueryProgress(QUERY_PROGRESS_FAILED);
        throw new Error(error);
      }
      if (!consultation?.id) {
        const error = `Consultation passed to useClinicalConsultationAction is missing for actionType ${actionType}`;
        console.error(error);
        setQueryProgress(QUERY_PROGRESS_FAILED);
        throw new Error(error);
      }

      try {
        console.log({
          clinical_consultation_id: consultation.id,
          context,
        });
        const { data } = await consultationAction(
          {
            clinical_consultation_id: consultation.id,
            context,
          } as ActionPayload<A>,
          {
            consultationId: consultation.id,
            patientId: patient.id,
          },
        );

        const result = data?.clinical_consultation ?? null;

        if (!result) throw new Error("No clinical consultation returned");

        const typedResult = result as ActionResult<A>;
        if (onCompleted) onCompleted(typedResult, client);
        setQueryProgress(QUERY_PROGRESS_SUCCEED);
        return typedResult;
      } catch (err) {
        setQueryProgress(QUERY_PROGRESS_FAILED);
        console.error(`${actionType} - `, getErrorMessage(err));
        if (onError) onError(err);
        return null;
      }
    },
    [actionType, consultationAction, client],
  );

  const reset = useCallback(() => {
    setQueryProgress(QUERY_PROGRESS_NOT_STARTED);
    resetProgress();
  }, [resetProgress]);

  return [callAction, queryProgress, reset];
};
